import { LocaleLink } from "@/components/LocaleLink";
import { useTranslation } from "react-i18next";
import {
  Shield, MapPin, Scale, RefreshCw, Receipt, CheckCircle2,
  Building2, Users, Clock, Briefcase, ArrowRight, Lock,
  Phone, Star, Wrench,
} from "lucide-react";
import SEOHead from "@/components/seo/SEOHead";
import JsonLd from "@/components/seo/JsonLd";
import LeadsHeader from "@/components/leads/LeadsHeader";
import Footer from "@/components/layout/Footer";
import Brand from "@/components/ui/brand";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import heroLeadsBg from "@/assets/hero-leads-bg.jpg";

export default function Leads() {
  const { t } = useTranslation();

  const trustBadges = [
    t("leads.trustDach"), t("leads.trustGdpr"), t("leads.trustMax5"),
    t("leads.trustPayOnAccept"), t("leads.trustNoSubs"), t("leads.trustCancelAnytime"),
  ];

  const steps = [
    { number: "01", title: t("leads.howStep1"), desc: t("leads.howStep1Desc") },
    { number: "02", title: t("leads.howStep2"), desc: t("leads.howStep2Desc") },
    { number: "03", title: t("leads.howStep3"), desc: t("leads.howStep3Desc") },
  ];

  const trustPoints = [
    { icon: Lock, text: t("leads.trust1") },
    { icon: Receipt, text: t("leads.trust2") },
    { icon: MapPin, text: t("leads.trust3") },
    { icon: Scale, text: t("leads.trust4") },
    { icon: RefreshCw, text: t("leads.trust5") },
  ];

  const exampleOpp = {
    title: t("leads.exampleName"),
    region: "München",
    gewerk: "Sanitär",
    budget: "5.000 – 10.000 €",
    urgency: "1 Monat",
    objectType: "Einfamilienhaus",
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={t("leads.seoTitle")}
        description={t("leads.seoDesc")}
        canonical="/leads"
      />
      <JsonLd
        includeService
        includeFaq
        breadcrumbs={[
          { name: t("common.home", { defaultValue: "Startseite" }), url: "https://www.craftfindus.com/" },
          { name: t("common.projectInquiries"), url: "https://www.craftfindus.com/leads" },
        ]}
      />

      <LeadsHeader />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroLeadsBg} alt="Handwerker bei der Arbeit" className="w-full h-full object-cover" loading="eager" fetchPriority="high" width={1920} height={1080} />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 section-padding pb-20 md:pb-24 pt-24 md:pt-32 w-full">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-display mb-6 opacity-0 animate-fade-up">
              {t("leads.heroTitle")}{" "}
              <span className="text-accent">{t("leads.heroAccent")}</span>
            </h1>
            <p className="text-body text-muted-foreground max-w-2xl mx-auto mb-10 text-lg opacity-0 animate-fade-up delay-100 whitespace-pre-line">
              {t("leads.heroDesc")}
            </p>
            <div className="flex items-center justify-center mb-4 opacity-0 animate-fade-up delay-200">
              <LocaleLink to="/registrieren" className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-accent-foreground font-medium text-base hover:bg-accent/90 transition-colors">
                {t("leads.heroCta")} <ArrowRight className="w-4 h-4" />
              </LocaleLink>
            </div>
            <p className="text-micro opacity-0 animate-fade-up delay-300">{t("leads.heroSubline")}</p>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8 opacity-0 animate-fade-up delay-400">
              {trustBadges.map((badge) => (
                <span key={badge} className="text-micro px-3 py-1.5 border border-border bg-card">{badge}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vorteile */}
      <section className="py-20 md:py-28 section-padding bg-card border-y border-border">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-headline text-center mb-16">
            {t("leads.benefitsTitle")} <span className="text-accent">{t("leads.benefitsAccent")}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { title: t("leads.benefit1Title"), desc: t("leads.benefit1Desc") },
              { title: t("leads.benefit2Title"), desc: t("leads.benefit2Desc") },
              { title: t("leads.benefit3Title"), desc: t("leads.benefit3Desc") },
            ].map((item) => (
              <div key={item.title} className="border border-border bg-background p-6 space-y-3">
                <CheckCircle2 className="w-6 h-6 text-accent" />
                <h3 className="text-subheadline">{item.title}</h3>
                <p className="text-caption leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <LocaleLink to="/registrieren" className="inline-flex items-center gap-2 text-sm font-medium text-accent hover:text-foreground transition-colors">
              {t("leads.benefitsCta")} <ArrowRight className="w-4 h-4" />
            </LocaleLink>
          </div>
        </div>
      </section>

      {/* So funktioniert's */}
      <section className="py-20 md:py-28 section-padding">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-headline text-center mb-4">{t("leads.howTitle1")} <span className="text-accent">{t("leads.howAccent")}</span></h2>
          <p className="text-center text-caption mb-16">{t("leads.howDesc", { brand: "CRAFTFINDUS" })}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <div key={step.number} className="space-y-4">
                <span className="text-4xl font-medium text-accent/20" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{step.number}</span>
                <h3 className="text-subheadline">{step.title}</h3>
                <p className="text-caption leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 border border-border">
            <div className="px-6 py-4 border-b border-border">
              <p className="text-sm font-medium tracking-wide uppercase text-accent">{t("leads.howBanner")}</p>
            </div>
            <Accordion type="single" collapsible className="px-6">
              <AccordionItem value="q1">
                <AccordionTrigger className="text-sm">{t("leads.faqQ1")}</AccordionTrigger>
                <AccordionContent className="text-caption">{t("leads.faqA1")}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="q2">
                <AccordionTrigger className="text-sm">{t("leads.faqQ2")}</AccordionTrigger>
                <AccordionContent className="text-caption">{t("leads.faqA2")}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="q3" className="border-b-0">
                <AccordionTrigger className="text-sm">{t("leads.faqQ3")}</AccordionTrigger>
                <AccordionContent className="text-caption">{t("leads.faqA3")}</AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Vertrauen */}
      <section className="py-20 md:py-28 section-padding bg-card border-y border-border">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-headline text-center mb-16">
            {t("leads.trustTitle")} <span className="text-accent">{t("leads.trustAccent", { brand: "CRAFTFINDUS" })}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
            {trustPoints.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-4 p-5 border border-border bg-background">
                <Icon className="w-5 h-5 text-accent shrink-0" />
                <span className="text-body">{text}</span>
              </div>
            ))}
          </div>
          <div className="text-center">
            <LocaleLink to="/registrieren" className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-accent-foreground font-medium text-base hover:bg-accent/90 transition-colors">
              {t("leads.trustCta")} <ArrowRight className="w-4 h-4" />
            </LocaleLink>
          </div>
        </div>
      </section>

      {/* Beispiel */}
      <section className="py-20 md:py-28 section-padding">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-headline text-center mb-12">
            {t("leads.exampleTitle")} <span className="text-accent">{t("leads.exampleAccent")}</span>{t("leads.exampleSuffix")}
          </h2>
          <div className="opportunity-card mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="status-dot status-dot-active" />
              <span className="text-micro">{t("leads.exampleLabel")}</span>
            </div>
            <h3 className="text-subheadline mb-6">{exampleOpp.title}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[
                { icon: MapPin, label: t("leads.regionLabel"), value: exampleOpp.region },
                { icon: Wrench, label: t("leads.tradeLabel"), value: exampleOpp.gewerk },
                { icon: Briefcase, label: t("leads.budgetLabel"), value: exampleOpp.budget },
                { icon: Clock, label: t("leads.urgencyLabel"), value: exampleOpp.urgency },
                { icon: Building2, label: t("leads.objectLabel"), value: exampleOpp.objectType },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="space-y-1">
                  <div className="flex items-center gap-1.5 text-micro"><Icon className="w-3.5 h-3.5" />{label}</div>
                  <p className="text-sm text-foreground">{value}</p>
                </div>
              ))}
            </div>
            <div className="divider-line my-6" />
            <p className="text-caption text-center" dangerouslySetInnerHTML={{ __html: t("leads.exampleMax5") }} />
          </div>
          <div className="text-center">
            <LocaleLink to="/registrieren" className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 transition-colors">
              {t("leads.exampleCta")} <ArrowRight className="w-4 h-4" />
            </LocaleLink>
          </div>
        </div>
      </section>

      {/* Sicherheit */}
      <section className="py-20 md:py-28 section-padding bg-card border-y border-border">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-headline mb-12">{t("leads.securityTitle")} <span className="text-accent">{t("leads.securityAccent")}</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12 text-left">
            {[
              { icon: Shield, text: t("leads.security1") },
              { icon: Lock, text: t("leads.security2") },
              { icon: Phone, text: t("leads.security3") },
              { icon: Receipt, text: t("leads.security4") },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-4 p-5 border border-border bg-background">
                <Icon className="w-5 h-5 text-primary shrink-0" />
                <span className="text-body">{text}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-1 text-accent">
            {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-5 h-5 fill-accent" />)}
            <span className="text-caption ml-2">{t("leads.securityTrust")}</span>
          </div>
        </div>
      </section>

      {/* Finaler CTA */}
      <section className="py-24 md:py-32 section-padding">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-headline mb-6">{t("leads.ctaTitle")} <span className="text-accent">{t("leads.ctaAccent")}</span></h2>
          <p className="text-body text-muted-foreground mb-10 text-lg whitespace-pre-line">
            {t("leads.ctaDesc")}
          </p>
          <LocaleLink to="/registrieren" className="inline-flex items-center gap-2 px-10 py-5 bg-accent text-accent-foreground font-medium text-lg hover:bg-accent/90 transition-colors">
            {t("leads.ctaBtn")} <ArrowRight className="w-5 h-5" />
          </LocaleLink>
          <p className="text-micro mt-6">{t("leads.ctaSubline")}</p>
          <nav className="mt-12 flex flex-wrap items-center justify-center gap-6 text-caption" aria-label="Weiterführende Links">
            <LocaleLink to="/fuer-handwerker" className="link-subtle hover:text-foreground transition-colors">{t("leads.ctaForCraft")}</LocaleLink>
            <LocaleLink to="/suche" className="link-subtle hover:text-foreground transition-colors">{t("leads.ctaForSearch")}</LocaleLink>
            <LocaleLink to="/ueber-uns" className="link-subtle hover:text-foreground transition-colors">{t("leads.ctaAbout")}</LocaleLink>
          </nav>
        </div>
      </section>

      <Footer />
    </div>
  );
}
